import React, { useEffect, useRef, useState } from "react";
import $ from "jquery";
import ConfirmModal from '../modals/ConfirmModal.jsx';
import useMarketPlaceModal from "../../pages/Layout/DashboardLayout/MarketPlaceModal/useMarketPlaceModal";
import { locationWiseData } from '../../services/regionalLevelSales';
import { dummyImage } from "../../../images/index.js";

const NormalTable = ({
  tableId,
  data,
  selectedIds = { categoryId: [], productId: [] },
  onSelectionChange,
  idKey = "id",
  typeKey = "type",
  enableSelection = true,
  prioritizeSelected = false,
  defaultPageLength = 10,
  refreshTrigger,
  onCategoryClick,
  onParentProductClick,
  loading = false,
  sequence,
  filterData = {},
  title,
  downloadBtnPermission = true
}) => {
  const containerRef = useRef(null);
  const selectedIdsRef = useRef(new Set());
  const [delayedEmpty, setDelayedEmpty] = useState(false);

  const { formattedStart, formattedEnd, level } = filterData;

  useEffect(() => {
    if (!loading) {
      if (Array.isArray(data) && data.length === 0) {
        setDelayedEmpty(true);
      } else {
        setDelayedEmpty(false);
      }
    } else {
      setDelayedEmpty(false);
    }
  }, [loading, data]);


  useEffect(() => {
    const flatSelected = [
      ...(selectedIds.productId || []),
      ...(selectedIds.categoryId || [])
    ];
    selectedIdsRef.current = new Set(flatSelected);
  }, [selectedIds]);

  const parseCurrency = (value) => {
    if (typeof value !== "string" && typeof value !== "number") return null;

    // Normalize and clean input
    let str = String(value).trim().replace(/[₹,%\s]/g, '').toUpperCase();

    // Handle Crores
    if (str.endsWith('CR')) {
      const num = parseFloat(str.replace('CR', ''));
      return isNaN(num) ? null : num * 10000000;
    }

    // Handle Lakhs
    if (str.endsWith('L')) {
      const num = parseFloat(str.replace('L', ''));
      return isNaN(num) ? null : num * 100000;
    }

    // Handle Thousands
    if (str.endsWith('K')) {
      const num = parseFloat(str.replace('K', ''));
      return isNaN(num) ? null : num * 1000;
    }

    // Handle plain numeric with commas (e.g., "1,962")
    str = str.replace(/,/g, '');
    const numeric = parseFloat(str);
    return isNaN(numeric) ? null : numeric;
  };


  useEffect(() => {
    const initialize = async () => {
      if (!containerRef.current) return;

      try {
        const existingTable = $(`#${tableId}`);
        if ($.fn.DataTable.isDataTable(existingTable)) {
          existingTable.DataTable().clear().destroy(true);
          existingTable.remove();
        }

        if (loading) {
          containerRef.current.innerHTML = getSkeletonTableHTML();
          return;
        }
        if (!loading && (!Array.isArray(data) || data.length === 0)) {
          containerRef.current.innerHTML = getNoDataHTML();
          return;
        }

        if (!Array.isArray(data) || data.length === 0) {
          if (delayedEmpty) containerRef.current.innerHTML = "";
          return;
        }

        await import("../../../datatable.js");

        const sortByType = (a, b) => {
          const priority = { Category: 0, Product: 1 };
          return (priority[a.type] ?? 2) - (priority[b.type] ?? 2);
        };

        const processedData = prioritizeSelected
          ? [...data].sort((a, b) => {
            const idA = a[idKey]?.toString();
            const idB = b[idKey]?.toString();
            const selectedDiff =
              Number(selectedIdsRef.current.has(idB)) -
              Number(selectedIdsRef.current.has(idA));
            if (selectedDiff !== 0) return selectedDiff;
            return sortByType(a, b);
          })
          : [...data].sort(sortByType);

        const showASIN_SKU = ["productGrowthId", "salesByState", "topSellingProducts", "topSellingCategory"].includes(tableId);

        let headers = Object.keys(processedData[0] || {}).filter((h) => {
          const excludeFields = ["type", "image", "net_sales_cents", "fourth_comparison", "second_comparison", "third_comparison","actual_units"];
          if (!showASIN_SKU) {
            excludeFields.push("asin", "sku", "brand");
          }
          return !excludeFields.includes(h);
        });

        // Apply sequence.columns if defined and is a valid array
        if (Array.isArray(sequence?.columns) && sequence.columns.length > 0) {
          headers = sequence.columns.filter((col) => headers.includes(col));
        }


        const tableHTML = `

        <div class="table-scroll-wrapper" id="scroll-wrapper-${tableId}">
          <table id="${tableId}" class="display table freeze-table" style="width:100%">
            <thead class="table-header">
              <tr>
                ${enableSelection ? `<th class="sticky-col checkbox-col not_export table-header-content"><input type="checkbox" id="select-all-on-page" /></th>` : ""}
                ${headers
            .filter(h => h !== idKey)
            .map((h, idx) => {
              let label = h
                .replace(/_/g, " ")
                .split(" ")
                .map(word => {
                  const lower = word.toLowerCase();
                  if (lower === "percentage") return "%";
                  if (lower === "ads") return "Ads";
                  if (lower === "tacos") return "TACoS";
                  if (lower === "wow") return "WoW%";
                  if (lower === "mom") return "MoM%";
                  return word.toUpperCase();
                })
                .join(" ");
              const extraClass = (!enableSelection && idx === 0) || (enableSelection && idx === 0)
                ? "sticky-col name-col table-header"
                : "table-header";
              return `<th class="${extraClass}">${label}</th>`;
            })
            .join("")}
              </tr>
            </thead>
            <tbody style="vertical-align: middle;"> 
              ${processedData
            .map(row => {
              const rowId = row[idKey]?.toString() || "";
              const rowType = row[typeKey]?.toString() || "";
              const isChecked = selectedIdsRef.current.has(rowId) ? "checked" : "";

              return `
                    <tr data-id="${rowId}" data-type="${rowType}">
                      ${enableSelection
                  ? `<td class="sticky-col checkbox-col not_export table-header-content"><input type="checkbox" class="row-checkbox normal-custom-checkbox" data-id="${rowId}" data-type="${rowType}" ${isChecked} /></td>`
                  : ""
                }
                      ${headers
                  .filter(key => key !== idKey)
                  .map((key, idx) => {
                    const cellValue = row[key];
                    const orderValue = parseCurrency(cellValue);
                    const extraClass = (!enableSelection && idx === 0) || (enableSelection && idx === 0)
                      ? "sticky-col name-col table-header-content"
                      : "";

                    const cellContent =
                      key === "type" || key === "image" || key === "net_sales_cents"
                        ? ""
                        : key === "streak"
                          ? (() => {
                            const streakCount = Number(row[key]) || 0;
                            return `
                                    <div style="display:flex; gap:6px;">
                                      ${Array.from({ length: streakCount })
                                .map(
                                  () =>
                                    `<span style="width:10px; height:10px; border-radius:50%; background:#34c759; display:inline-block;"></span>`
                                )
                                .join("")}
                                    </div>
                                  `;
                          })()
                          : key === "name"
                            ? `<div style="display: flex; align-items: center; gap: 10px;">
                               ${!row.hasOwnProperty("image")
                              ? "" :
                              row["image"] || row["type"] === "Product" || row["type"] === "Parent Product"
                                ? `<img src="${row["image"] && row["image"].trim() !== "" ? row["image"] : dummyImage
                                }" alt="${row["parent_category"] || "image"}" width="70" height="70" style="border-radius:4px;object-fit:cover; " />`
                                : (row["image"]?.trim() === "" || row["image"] === null) ? `<img src="${dummyImage}" width="70" height="70" class="dummy-img"/>` : `` //`<img width="70" height="70" class="dummy-img"/>`
                            }
                                <div style="min-width:200px; font-size:14px; line-height:1.4;">
                                  ${row?.asin?.trim?.() || row?.sku?.trim?.() || row?.brand?.trim?.()
                              ? (() => {
                                // Determine clickable value: ASIN first, then SKU
                                const isAsinClickable = row?.asin?.trim?.();
                                const isSkuClickable = !isAsinClickable && row?.sku?.trim?.();

                                return `
                                            ${isAsinClickable
                                    ? `<div style="cursor:pointer; color:#007bff;" onclick="window.open('https://www.amazon.in/dp/${row["asin"]}', '_blank')">
                                                     ASIN: ${row["asin"]}
                                                   </div>`
                                    : ""
                                  }
                                            ${isSkuClickable
                                    ? `<div style="cursor:pointer; color:#007bff;" onclick="window.open('https://www.amazon.in/s?k=${row["sku"]}', '_blank')">
                                                     SKU: ${row["sku"]}
                                                   </div>`
                                    : row?.sku?.trim?.() && isAsinClickable
                                      ? `<div>SKU: ${row["sku"]}</div>`
                                      : ""
                                  }
                                            ${row?.brand?.trim?.()
                                    ? `<div style="color:#555;">
                                                     Brand: ${row["brand"]}
                                                   </div>`
                                    : ""
                                  }
                                          `;
                              })()
                              : ""
                            }
                                </div>

                               </div>`
                            : key === "wow" || key === "mom" || key === "first_comparison" && typeof cellValue === "number"
                              ? `<b><span style="color:${cellValue < 0 ? "#ff4d4f" : "#34c759"}">
                                   ${cellValue > 0 ? `+${cellValue}` : cellValue}%
                                 </span></b>`
                              : cellValue ?? "";



                    return orderValue == null
                      ? `<td class="${extraClass}">${cellContent}</td>`
                      : `<td class="${extraClass}" data-order="${orderValue}">${cellContent}</td>`;
                  })
                  .join("")}
                    </tr>`;
            })
            .join("")}
            </tbody>
          </table>
        `;

        containerRef.current.innerHTML = tableHTML;

        const idColIndex = headers.findIndex(h => h === idKey);
        const checkboxOffset = enableSelection ? 1 : 0;

        window.productGrowthDataTable({
          selector: `#${tableId}`,
          custom_class: "normalTable",
          pageLength: defaultPageLength,
          ordering: true,
          order: [],
          title: title,
          columnDefs: [
            ...(enableSelection ? [{ targets: 0, orderable: false }] : []),
            { targets: idColIndex + checkboxOffset, visible: false },
          ],
          downloadBtnPermission
        });

        const $table = $(`#${tableId}`).DataTable();
        $table.draw();


      } catch (err) {
        console.error("Error initializing DataTable:", err);
      }
    };

    initialize();

    return () => {
      const existingTable = $(`#${tableId}`);
      if ($.fn.DataTable.isDataTable(existingTable)) {
        existingTable.DataTable().clear().destroy(true);
        existingTable.remove();
      }
      $(`#${tableId}`).off();
    };
  }, [data, tableId, idKey, enableSelection, defaultPageLength, refreshTrigger, loading]);

  const getNoDataHTML = () => {
    return `
      <div class="no-data p-5" style="text-align: center; font-weight: 500;">
        No data found
      </div>
    `;
  };

  const getSkeletonTableHTML = () => {
    const dynamicLevel = filterData?.level || "default";
    const sequence = {
      top10: ["NAME", "ACTUAL UNITS", "WoW%", "NET SALES"],
      low10: ["NAME", "ACTUAL UNITS", "WoW%", "NET SALES"],
      fastestGrowing: ["NAME", "ACTUAL UNITS", "WoW%", "NET SALES"],
      declining: ["NAME", "ACTUAL UNITS", "WoW%", "NET SALES"],
      heroes: ["NAME", "NET SALES", "ACTUAL UNITS", "Wow%", "STREAK"],
    };

    // Pick headers based on tableId, or fallback to a default set
    const headers = sequence[tableId] || ["NAME", "ACTUAL UNITS", "WoW%", "NET SALES"];

    const skeletonRow = `
      <tr>
        ${enableSelection ? "<td><div class='skeleton-box skeleton-checkbox'></div></td>" : ""}
        ${headers.map((_, idx) => {
      if (idx === 0) {
        return `
              <td>
                <div class="skeleton-product">
                  <div class="skeleton-image"></div>
                  <div class="skeleton-text-group">
                    <div class="skeleton-title"></div>
                    <div class="skeleton-subtitle"></div>
                  </div>
                </div>
              </td>`;
      }
      return "<td><div class='skeleton-box'></div></td>";
    }).join("")}
      </tr>`;

    return `
    <div class="table-wrapper">
      <div class="table-toolbar">
        <div class="search-placeholder">
          <div class="skeleton-search-icon"></div>
          <div class="skeleton-search-input"></div>
        </div>
        <div class="export-icons">
          <div class="skeleton-icon"></div>
          <div class="skeleton-icon"></div>
        </div>
      </div>
      <table id="${tableId}" class="display table" style="width:100%">
        <thead class="table-header">
          <tr>
            ${enableSelection ? "<th class='skeleton-th'></th>" : ""}
            ${headers.map(h => `<th class='skeleton-th'>${h}</th>`).join("")}
          </tr>
        </thead>
        <tbody>
          ${Array.from({ length: 10 }).map(() => skeletonRow).join("")}
        </tbody>
      </table>
    </div>
    `;
  };

  const showNoDataMessage = delayedEmpty;

  return (
    <>
      <div ref={containerRef} />
    </>
  );
};

export default NormalTable;
